import React, { useEffect, useState } from 'react';

/**
 * BootSplash — full-screen hero composition that takes over the very first
 * frame after the OS PWA splash dismisses. Shows for ~1500ms then fades out
 * over 400ms to reveal the underlying app.
 *
 * Designed to bridge the gap between the OS-generated PWA splash (which is
 * locked to ~25% icon size + plain background_color) and the dashboard,
 * delivering the brand-confident full hero composition: dragon-scale texture
 * background + flame-E + NFERNO wordmark + tagline + ember particles.
 *
 * Honors prefers-reduced-motion (skips the entrance animation but still
 * displays the still composition before fading).
 *
 * Session-scoped: once shown for a tab/session, sessionStorage flag prevents
 * re-showing on every route navigation. Always shows on a fresh tab open.
 */
const SESSION_KEY = 'enferno_boot_splash_shown_v1';
const HOLD_MS = 1500;
const FADE_MS = 400;

const BootSplash = () => {
  const [visible, setVisible] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return sessionStorage.getItem(SESSION_KEY) !== '1'; } catch { return true; }
  });
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (!visible) return;
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ }
    // Suppress ambient dragon-scale background while splash is showing —
    // the splash composes its own scale texture as the hero treatment.
    document.body.classList.add('no-dragon-scales');
    const fadeTimer = setTimeout(() => setFading(true), HOLD_MS);
    const removeTimer = setTimeout(() => {
      setVisible(false);
      document.body.classList.remove('no-dragon-scales');
    }, HOLD_MS + FADE_MS);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
      document.body.classList.remove('no-dragon-scales');
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      data-testid="boot-splash"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: '#000',
        opacity: fading ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease-in-out`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: fading ? 'none' : 'auto',
      }}
    >
      {/* Dragon-scale backdrop — mirrors the ambient app backdrop so "0.13"
          means the same thing in both places. Texture as its own layer at
          opacity 0.13, no blend mode, no darkening overlay. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'url(/generated/dragon-scale-texture.png)',
          backgroundSize: '480px 480px',
          backgroundRepeat: 'repeat',
          opacity: 0.30,
          pointerEvents: 'none',
        }}
      />

      {/* Ember particles via CSS-only animated gradient — keeps splash
          asset count down. Static positions; opacity oscillates so they
          look alive without being distracting. */}
      <div aria-hidden style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        {EMBERS.map((e, i) => (
          <span
            key={i}
            style={{
              position: 'absolute',
              left: `${e.x}%`,
              top: `${e.y}%`,
              width: `${e.size}px`,
              height: `${e.size}px`,
              borderRadius: '50%',
              background: e.color,
              filter: `blur(${e.blur}px)`,
              opacity: e.opacity,
              animation: `enferno-ember-flicker ${e.dur}s ease-in-out infinite`,
              animationDelay: `${e.delay}s`,
            }}
          />
        ))}
      </div>

      {/* Wordmark + tagline content */}
      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', padding: '0 24px' }}>
        <img
          src="/brand/enferno-wordmark.png"
          alt="Enferno"
          style={{
            width: 'min(60vw, 420px)',
            height: 'auto',
            display: 'block',
            margin: '0 auto 32px',
            animation: 'enferno-boot-rise 700ms cubic-bezier(0.16, 1, 0.3, 1) both',
          }}
        />
        <div
          style={{
            color: '#f5f5f5',
            fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
            fontWeight: 600,
            fontSize: 'clamp(20px, 5vw, 32px)',
            letterSpacing: '0.08em',
            animation: 'enferno-boot-fade 900ms ease-out 200ms both',
          }}
        >
          Bring the heat.
        </div>
      </div>

      <style>{`
        @keyframes enferno-boot-rise {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes enferno-boot-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes enferno-ember-flicker {
          0%, 100% { opacity: 0.3; transform: scale(0.9); }
          50%      { opacity: 0.8; transform: scale(1.1); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="boot-splash"] *,
          [data-testid="boot-splash"] *::before,
          [data-testid="boot-splash"] *::after {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>
    </div>
  );
};

// Hand-tuned ember positions (avoiding centered wordmark zone ~40-60% y).
// Sized to feel like sparks rising from the lower third of the canvas.
const EMBERS = [
  { x: 12, y: 78, size: 6,  color: '#dc2626', blur: 2, opacity: 0.6, dur: 2.4, delay: 0   },
  { x: 22, y: 88, size: 4,  color: '#f97316', blur: 1, opacity: 0.7, dur: 2.0, delay: 0.3 },
  { x: 35, y: 72, size: 8,  color: '#dc2626', blur: 3, opacity: 0.5, dur: 2.8, delay: 0.7 },
  { x: 48, y: 90, size: 5,  color: '#ffa53c', blur: 2, opacity: 0.65, dur: 2.2, delay: 1.1 },
  { x: 62, y: 80, size: 7,  color: '#f97316', blur: 2, opacity: 0.55, dur: 2.6, delay: 0.5 },
  { x: 75, y: 85, size: 4,  color: '#dc2626', blur: 1, opacity: 0.7, dur: 2.0, delay: 0.9 },
  { x: 88, y: 75, size: 6,  color: '#ffa53c', blur: 2, opacity: 0.55, dur: 2.5, delay: 1.3 },
  { x: 18, y: 30, size: 3,  color: '#f97316', blur: 1, opacity: 0.4, dur: 3.0, delay: 0.4 },
  { x: 82, y: 25, size: 4,  color: '#dc2626', blur: 1, opacity: 0.45, dur: 2.7, delay: 1.0 },
  { x: 8,  y: 60, size: 3,  color: '#f97316', blur: 1, opacity: 0.4, dur: 2.3, delay: 0.6 },
  { x: 92, y: 55, size: 4,  color: '#ffa53c', blur: 1, opacity: 0.45, dur: 2.9, delay: 0.2 },
];

export default BootSplash;
